import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { CalendarIcon, ChevronRight, Download, Loader2 } from "lucide-react";
import { REPORT_DATE_TYPES } from "../assets/constants";
import { observer } from "mobx-react-lite";
import dateStore from "../hooks/useDate";
import { useReportDownload } from "../hooks/useReportDownload";
import "react-datepicker/dist/react-datepicker.css";

const CustomInput = React.forwardRef(
  ({ value, onClick, startDate, endDate }, ref) => {
    const formatDisplayDate = (date) => {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const displayText =
      startDate && endDate
        ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
        : "Select dates";

    return (
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className="inline-flex items-center justify-between w-full md:min-w-[220px] rounded-md bg-white px-3 py-1.5 text-xs text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
      >
        <div className="inline-flex items-center gap-2">
          <CalendarIcon className="h-3.5 w-3.5 text-gray-500" />
          <span>{displayText}</span>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
      </button>
    );
  }
);

CustomInput.displayName = "CustomInput";

const DateFilter = observer(({ className = "", onDateChange, reportType }) => {
  const { dateType, startDate, endDate } = dateStore;
  const { initiateDownload, getRequest, checkDownloadStatus, clearRequest } =
    useReportDownload();
  const [dateRange, setDateRange] = useState([
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null,
  ]);

  useEffect(() => {
    setDateRange([startDate, endDate]);
  }, [startDate, endDate]);

  useEffect(() => {
    if (reportType) {
      checkDownloadStatus(reportType);
    }
  }, [reportType]);

  const formatLocalDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateRangeChange = (update) => {
    setDateRange(update);
    if (update[0] && update[1]) {
      const formattedStart = formatLocalDate(update[0]);
      const formattedEnd = formatLocalDate(update[1]);
      dateStore.setCustomDateRange(formattedStart, formattedEnd);
      onDateChange({
        startDate: formattedStart,
        endDate: formattedEnd,
        dateType: dateType,
      });
    }
  };

  const handleDownloadClick = () => {
    if (!currentRequest?.downloadUrl) return;

    // Open download in new tab
    window.open(currentRequest.downloadUrl, "_blank");

    // Clear the request from store
    clearRequest(reportType);
  };

  const handleDownload = async () => {
    if (!reportType) return;

    await initiateDownload({
      reportType,
      dateType,
      startDate,
      endDate,
      label: `${reportType} Report`,
    });
  };

  const currentRequest = reportType ? getRequest(reportType) : null;
  const isPending = currentRequest?.status === "pending";
  const isCompleted = currentRequest?.status === "completed";

  useEffect(() => {
    let pollingInterval;

    const checkStatus = async () => {
      if (!reportType) return;
      
      const result = await checkDownloadStatus(reportType);
      if (result?.success && result?.status === "completed" && result?.downloadUrl) {
        // Automatically open download and clear request
        window.open(result.downloadUrl, '_blank');
        clearRequest(reportType);
        
        // Clear polling interval
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      }
    };

    if (isPending && reportType) {
      // Initial check
      checkStatus();
      
      // Set up polling every 10 seconds
      pollingInterval = setInterval(checkStatus, 10000);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isPending, reportType]);

  return (
    <div className={`flex flex-col md:flex-row justify-end items-start md:items-center gap-3 ${className}`}>
      {/* Mobile: Vertical layout, Desktop: Horizontal layout */}
      <div className="w-full md:w-auto">
        <div className="grid grid-cols-2 md:inline-flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-300 gap-1 md:gap-0">
                  <button
            type="button"
            onClick={() => {
              dateStore.setDateType(REPORT_DATE_TYPES.TODAY);
              onDateChange({
                startDate: dateStore.startDate,
                endDate: dateStore.endDate,
                dateType: dateStore.dateType,
              });
            }}
            className={`px-2 py-1.5 md:px-2.5 md:py-1 text-xs font-medium rounded-md transition-all duration-200 ${
              dateType === REPORT_DATE_TYPES.TODAY
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => {
              dateStore.setDateType(REPORT_DATE_TYPES.YESTERDAY);
              onDateChange({
                startDate: dateStore.startDate,
                endDate: dateStore.endDate,
                dateType: dateStore.dateType,
              });
            }}
            className={`px-2 py-1.5 md:px-2.5 md:py-1 text-xs font-medium rounded-md transition-all duration-200 ${
              dateType === REPORT_DATE_TYPES.YESTERDAY
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            Yesterday
          </button>
          <button
            type="button"
            onClick={() => {
              dateStore.setDateType(REPORT_DATE_TYPES.LAST_7_DAYS);
              onDateChange({
                startDate: dateStore.startDate,
                endDate: dateStore.endDate,
                dateType: dateStore.dateType,
              });
            }}
            className={`px-2 py-1.5 md:px-2.5 md:py-1 text-xs font-medium rounded-md transition-all duration-200 ${
              dateType === REPORT_DATE_TYPES.LAST_7_DAYS
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            Week
          </button>
          <button
            type="button"
            onClick={() => {
              dateStore.setDateType(REPORT_DATE_TYPES.LAST_30_DAYS);
              onDateChange({
                startDate: dateStore.startDate,
                endDate: dateStore.endDate,
                dateType: dateStore.dateType,
              });
            }}
            className={`px-2 py-1.5 md:px-2.5 md:py-1 text-xs font-medium rounded-md transition-all duration-200 ${
              dateType === REPORT_DATE_TYPES.LAST_30_DAYS
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            Month
          </button>
        </div>
      </div>

      <div className="relative z-10 w-full md:w-auto">
        <DatePicker
          selected={dateRange[0]}
          onChange={handleDateRangeChange}
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          selectsRange
          dateFormat="MMM dd, yyyy"
          customInput={<CustomInput startDate={startDate} endDate={endDate} />}
          wrapperClassName="!block"
          popperClassName="react-datepicker-popper"
          popperPlacement="bottom-end"
          maxDate={new Date()}
          minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          calendarClassName="shadow-lg border border-gray-300 rounded-lg"
          placeholderText="Select date range within past 3 months"
        />
      </div>

      {reportType && (
        <button
          type="button"
          onClick={isPending ? undefined : isCompleted ? () => window.open(currentRequest.downloadUrl, '_blank') : handleDownload}
          disabled={isPending}
          className={`w-full md:w-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
            isPending
              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
              : isCompleted
              ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
              : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-inset ring-gray-200"
          }`}
        >
          {isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : isCompleted ? (
            <>
              <Download className="h-3.5 w-3.5" />
              <span>Report Ready</span>
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5" />
              <span>Download Report</span>
            </>
          )}
        </button>
      )}
    </div>
  );
});

export default DateFilter;
